/**
 * The console's health report: what this admin console shows, saved as one
 * JSON file for review.
 *
 * Shaped like `scripts/prod-snapshot.mjs`'s output, which an operator runs with
 * admin AWS credentials, and judged by the same `healthFindings`. A reviewer
 * holding both can check that what the console shows matches what AWS and the
 * table hold. Sections the console cannot see (queue depths, Lambda errors,
 * EventBridge state) are simply absent here.
 *
 * No subscriber addresses: campaigns are ids and counters, audiences are counts.
 * Each read is independent and a failed one is recorded under `errors` rather
 * than sinking the report.
 */
import { healthFindings, type HealthFinding, type HealthSnapshot } from "@addressium/domain";
import { api, scheduleHasSent } from "./api.js";

/** Counters are one request per campaign; cap it so a report stays quick. */
export const REPORT_CAMPAIGN_LIMIT = 40;

export interface ConsoleHealthReport extends HealthSnapshot {
  kind: "addressium-console-report";
  version: 1;
  generatedAt: string;
  orgId: string;
  consoleUrl: string;
  health?: unknown;
  setup?: unknown;
  usage?: unknown;
  trends?: unknown;
  errors: Array<{ area: string; error: string }>;
  findings: HealthFinding[];
}

export async function buildHealthReport(org: string, now = new Date()): Promise<ConsoleHealthReport> {
  const errors: ConsoleHealthReport["errors"] = [];
  const read = async <T,>(area: string, fn: () => Promise<T>): Promise<T | undefined> => {
    try { return await fn(); } catch (e) { errors.push({ area, error: String(e) }); return undefined; }
  };
  const [health, identity, setup, meta, lists, schedules, campaigns, series, trends, usage] = await Promise.all([
    read("health", () => api.health(org)),
    read("ses", () => api.sendingIdentity(org)),
    read("setup", () => api.setup(org)),
    read("org", () => api.orgMeta(org)),
    read("lists", () => api.lists(org)),
    read("schedules", () => api.schedules(org)),
    read("campaigns", () => api.campaigns(org)),
    read("series", () => api.series(org)),
    read("trends", () => api.analyticsTrends(org, 30)),
    read("usage", () => api.usage(org)),
  ]);
  const since = new Date(now.getTime() - 30 * 86_400_000).toISOString();
  const recent = (campaigns ?? [])
    .filter((c) => (c.createdAt ?? c.sendAt ?? "") >= since)
    .sort((a, b) => (b.createdAt ?? b.sendAt ?? "").localeCompare(a.createdAt ?? a.sendAt ?? ""));
  if (recent.length > REPORT_CAMPAIGN_LIMIT) errors.push({ area: "campaigns", error: `${recent.length} campaigns in 30 days; counters fetched for the newest ${REPORT_CAMPAIGN_LIMIT}` });
  const withCounters = await Promise.all(recent.slice(0, REPORT_CAMPAIGN_LIMIT).map(async (c) => {
    const report = await read(`report:${c.campaignId}`, () => api.report(org, c.campaignId));
    return { campaignId: c.campaignId, type: c.type, status: c.status, seriesId: c.seriesId, listId: c.listId, createdAt: c.createdAt, sendAt: c.sendAt, counters: report?.counters ?? { sent: c.sent } };
  }));
  const audience = (trends?.summary?.audience ?? undefined) || undefined;
  const snapshot: ConsoleHealthReport = {
    kind: "addressium-console-report",
    version: 1,
    generatedAt: now.toISOString(),
    orgId: org,
    consoleUrl: typeof location !== "undefined" ? location.origin : "",
    health: health && { status: health.status, alarmsInAlarm: health.alarmsInAlarm, checkedAt: health.checkedAt, reason: health.reason },
    // Only firing alarms reach the console, and only for roles that manage identity.
    alarms: (health?.alarms ?? []).map((a) => ({ name: a.name, state: "ALARM", description: a.description, reason: a.reason, since: a.since })),
    ses: identity && {
      account: identity.account,
      identities: [...identity.domains, ...(identity.pendingDomains ?? [])].map((d) => ({ orgId: org, domain: d.domain, verification: d.state, dkim: d.dkimStatus, mailFrom: d.mailFromStatus })),
    },
    orgs: [{
      orgId: org,
      ...(meta?.defaultTimezone ? { defaultTimezone: meta.defaultTimezone } : {}),
      ...(audience ? { audience } : {}),
      lists: (lists ?? []).map((l) => ({ listId: l.listId, name: l.name, visibility: (l as { visibility?: string }).visibility, optInPolicy: (l as { optInPolicy?: string }).optInPolicy, access: (l as { access?: string }).access, fromAddress: (l as { fromAddress?: string }).fromAddress })),
      schedules: (schedules ?? []).map((s) => ({ scheduleId: s.scheduleId, kind: s.kind, status: s.status, cron: s.cron, timezone: s.timezone, sendAt: s.sendAt, hasSent: scheduleHasSent(s), updatedAt: s.updatedAt })),
      campaigns: withCounters,
      series: (series ?? []).map((s) => ({ seriesId: s.seriesId, templateId: s.templateId, filledSlots: s.adSlotFills.map((f) => f.slot) })),
    } as NonNullable<HealthSnapshot["orgs"]>[number]],
    setup,
    usage: usage?.[0],
    trends: trends?.summary,
    errors,
    findings: [],
  };
  snapshot.findings = healthFindings(snapshot, now.getTime());
  return snapshot;
}

/** Save the report as `<org>-health-<timestamp>.json`. */
export function downloadReport(report: ConsoleHealthReport): void {
  const blob = new Blob([`${JSON.stringify(report, null, 2)}\n`], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${report.orgId}-health-${report.generatedAt.replace(/[-:]/g, "").replace(/\.\d+Z$/, "Z")}.json`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
